/**
 * Price Book Admin Page
 * 
 * Manages Labour Rates, Fixed Fees, and Service Packages.
 * Provides CRUD operations for all price book items.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Tab definitions
const TABS = [
  { id: 'labour', label: 'Labour Rates', icon: '⚙️' },
  { id: 'fees', label: 'Fixed Fees', icon: '📋' },
  { id: 'packages', label: 'Service Packages', icon: '📦' },
];

export default function PriceBookAdmin() {
  const [activeTab, setActiveTab] = useState('labour');
  const [labourRates, setLabourRates] = useState([]);
  const [fees, setFees] = useState([]);
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingItem, setEditingItem] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createType, setCreateType] = useState(null);

  // Fetch all price book data
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [labourRes, feesRes, packagesRes] = await Promise.all([
        fetch(`${API_URL}/api/price-book/labour-rates?include_inactive=true`),
        fetch(`${API_URL}/api/price-book/fees?include_inactive=true`),
        fetch(`${API_URL}/api/price-book/packages?include_inactive=true`),
      ]);
      
      if (labourRes.ok) setLabourRates(await labourRes.json());
      if (feesRes.ok) setFees(await feesRes.json());
      if (packagesRes.ok) setPackages(await packagesRes.json());
    } catch (error) {
      console.error('Error fetching price book data:', error);
      toast.error('Failed to load price book data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // ============== LABOUR RATES CRUD ==============
  const handleCreateLabourRate = async (data) => {
    try {
      const res = await fetch(`${API_URL}/api/price-book/labour-rates`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.detail || 'Failed to create labour rate');
      }
      toast.success('Labour rate created');
      fetchData();
      setShowCreateModal(false);
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleUpdateLabourRate = async (code, data) => {
    try {
      const res = await fetch(`${API_URL}/api/price-book/labour-rates/${code}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      if (!res.ok) throw new Error('Failed to update');
      toast.success('Labour rate updated');
      fetchData();
      setEditingItem(null);
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleDeleteLabourRate = async (code) => {
    if (!window.confirm('Deactivate this labour rate?')) return;
    try {
      const res = await fetch(`${API_URL}/api/price-book/labour-rates/${code}`, {
        method: 'DELETE',
      });
      if (!res.ok) throw new Error('Failed to deactivate');
      toast.success('Labour rate deactivated');
      fetchData();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleHardDeleteLabourRate = async (code, name) => {
    if (!window.confirm(`Permanently delete "${name}" (${code})?\n\nThis cannot be undone and the rate will no longer appear anywhere.`)) return;
    try {
      const res = await fetch(`${API_URL}/api/price-book/labour-rates/${code}?hard=true`, {
        method: 'DELETE',
      });
      if (!res.ok) throw new Error('Failed to delete');
      toast.success('Labour rate deleted');
      fetchData();
    } catch (error) {
      toast.error(error.message);
    }
  };

  // ============== FEES CRUD ==============
  const handleCreateFee = async (data) => {
    try {
      const res = await fetch(`${API_URL}/api/price-book/fees`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.detail || 'Failed to create fee');
      }
      toast.success('Fee created');
      fetchData();
      setShowCreateModal(false);
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleUpdateFee = async (code, data) => {
    try {
      const res = await fetch(`${API_URL}/api/price-book/fees/${code}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      if (!res.ok) throw new Error('Failed to update');
      toast.success('Fee updated');
      fetchData();
      setEditingItem(null);
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleDeleteFee = async (code) => {
    if (!window.confirm('Deactivate this fee?')) return;
    try {
      const res = await fetch(`${API_URL}/api/price-book/fees/${code}`, {
        method: 'DELETE',
      });
      if (!res.ok) throw new Error('Failed to deactivate');
      toast.success('Fee deactivated');
      fetchData();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleHardDeleteFee = async (code, name) => {
    if (!window.confirm(`Permanently delete "${name}" (${code})?\n\nThis cannot be undone.`)) return;
    try {
      const res = await fetch(`${API_URL}/api/price-book/fees/${code}?hard=true`, {
        method: 'DELETE',
      });
      if (!res.ok) throw new Error('Failed to delete');
      toast.success('Fee deleted');
      fetchData();
    } catch (error) {
      toast.error(error.message);
    }
  };

  // ============== PACKAGES CRUD ==============
  const handleCreatePackage = async (data) => {
    try {
      const res = await fetch(`${API_URL}/api/price-book/packages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.detail || 'Failed to create package');
      }
      toast.success('Package created');
      fetchData();
      setShowCreateModal(false);
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleUpdatePackage = async (code, data) => {
    try {
      const res = await fetch(`${API_URL}/api/price-book/packages/${code}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      if (!res.ok) throw new Error('Failed to update');
      toast.success('Package updated');
      fetchData();
      setEditingItem(null);
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleDeletePackage = async (code) => {
    if (!window.confirm('Deactivate this package?')) return;
    try {
      const res = await fetch(`${API_URL}/api/price-book/packages/${code}`, {
        method: 'DELETE',
      });
      if (!res.ok) throw new Error('Failed to deactivate');
      toast.success('Package deactivated');
      fetchData();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleHardDeletePackage = async (code, name) => {
    if (!window.confirm(`Permanently delete "${name}" (${code})?\n\nThis cannot be undone.`)) return;
    try {
      const res = await fetch(`${API_URL}/api/price-book/packages/${code}?hard=true`, {
        method: 'DELETE',
      });
      if (!res.ok) throw new Error('Failed to delete');
      toast.success('Package deleted');
      fetchData();
    } catch (error) {
      toast.error(error.message);
    }
  };

  // ============== RENDER FUNCTIONS ==============
  const renderLabourRatesTab = () => (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold text-slate-800">Labour Rates</h3>
        <button
          onClick={() => { setCreateType('labour'); setShowCreateModal(true); }}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          data-testid="create-labour-rate-btn"
        >
          + Add Labour Rate
        </button>
      </div>
      
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <table className="w-full">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Code</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Name</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Category</th>
              <th className="px-4 py-3 text-right text-xs font-semibold text-slate-600 uppercase">Rate (ex VAT)</th>
              <th className="px-4 py-3 text-right text-xs font-semibold text-slate-600 uppercase">VAT Rate</th>
              <th className="px-4 py-3 text-center text-xs font-semibold text-slate-600 uppercase">Status</th>
              <th className="px-4 py-3 text-right text-xs font-semibold text-slate-600 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {labourRates.map((rate) => (
              <tr key={rate.code} className={`hover:bg-slate-50 ${!rate.is_active ? 'opacity-50' : ''}`}>
                <td className="px-4 py-3 font-mono text-sm text-slate-600">{rate.code}</td>
                <td className="px-4 py-3 font-medium text-slate-800">{rate.name}</td>
                <td className="px-4 py-3 text-sm text-slate-500">{rate.labour_category || '—'}</td>
                <td className="px-4 py-3 text-right font-semibold text-slate-800">£{rate.rate_ex_vat.toFixed(2)}/hr</td>
                <td className="px-4 py-3 text-right text-slate-600">{(rate.vat_rate * 100).toFixed(0)}%</td>
                <td className="px-4 py-3 text-center">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${rate.is_active ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}`}>
                    {rate.is_active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td className="px-4 py-3 text-right space-x-2">
                  <button
                    onClick={() => setEditingItem({ type: 'labour', data: rate })}
                    className="px-3 py-1 text-sm bg-slate-100 text-slate-700 rounded hover:bg-slate-200"
                    data-testid={`edit-labour-${rate.code}`}
                  >
                    Edit
                  </button>
                  {rate.is_active && (
                    <button
                      onClick={() => handleDeleteLabourRate(rate.code)}
                      className="px-3 py-1 text-sm bg-amber-100 text-amber-700 rounded hover:bg-amber-200"
                      data-testid={`deactivate-labour-${rate.code}`}
                    >
                      Deactivate
                    </button>
                  )}
                  <button
                    onClick={() => handleHardDeleteLabourRate(rate.code, rate.name)}
                    className="px-3 py-1 text-sm bg-red-100 text-red-700 rounded hover:bg-red-200"
                    data-testid={`delete-labour-${rate.code}`}
                    title="Permanently delete"
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderFeesTab = () => (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold text-slate-800">Fixed Fees</h3>
        <button
          onClick={() => { setCreateType('fee'); setShowCreateModal(true); }}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          data-testid="create-fee-btn"
        >
          + Add Fee
        </button>
      </div>
      
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <table className="w-full">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Code</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Name</th>
              <th className="px-4 py-3 text-right text-xs font-semibold text-slate-600 uppercase">Price (ex VAT)</th>
              <th className="px-4 py-3 text-right text-xs font-semibold text-slate-600 uppercase">VAT Rate</th>
              <th className="px-4 py-3 text-center text-xs font-semibold text-slate-600 uppercase">Status</th>
              <th className="px-4 py-3 text-right text-xs font-semibold text-slate-600 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {fees.map((fee) => (
              <tr key={fee.code} className={`hover:bg-slate-50 ${!fee.is_active ? 'opacity-50' : ''}`}>
                <td className="px-4 py-3 font-mono text-sm text-slate-600">{fee.code}</td>
                <td className="px-4 py-3 font-medium text-slate-800">{fee.name}</td>
                <td className="px-4 py-3 text-right font-semibold text-slate-800">£{fee.default_price_ex_vat.toFixed(2)}</td>
                <td className="px-4 py-3 text-right text-slate-600">{(fee.vat_rate * 100).toFixed(0)}%</td>
                <td className="px-4 py-3 text-center">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${fee.is_active ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}`}>
                    {fee.is_active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td className="px-4 py-3 text-right space-x-2">
                  <button
                    onClick={() => setEditingItem({ type: 'fee', data: fee })}
                    className="px-3 py-1 text-sm bg-slate-100 text-slate-700 rounded hover:bg-slate-200"
                    data-testid={`edit-fee-${fee.code}`}
                  >
                    Edit
                  </button>
                  {fee.is_active && (
                    <button
                      onClick={() => handleDeleteFee(fee.code)}
                      className="px-3 py-1 text-sm bg-amber-100 text-amber-700 rounded hover:bg-amber-200"
                      data-testid={`deactivate-fee-${fee.code}`}
                    >
                      Deactivate
                    </button>
                  )}
                  <button
                    onClick={() => handleHardDeleteFee(fee.code, fee.name)}
                    className="px-3 py-1 text-sm bg-red-100 text-red-700 rounded hover:bg-red-200"
                    data-testid={`delete-fee-${fee.code}`}
                    title="Permanently delete"
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderPackagesTab = () => (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold text-slate-800">Service Packages</h3>
        <button
          onClick={() => { setCreateType('package'); setShowCreateModal(true); }}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          data-testid="create-package-btn"
        >
          + Add Package
        </button>
      </div>
      
      <div className="grid gap-4">
        {packages.map((pkg) => (
          <div 
            key={pkg.code} 
            className={`bg-white rounded-xl border border-slate-200 p-4 ${!pkg.is_active ? 'opacity-50' : ''}`}
            data-testid={`package-card-${pkg.code}`}
          >
            <div className="flex justify-between items-start mb-3">
              <div>
                <div className="flex items-center gap-2">
                  <h4 className="font-semibold text-slate-800">{pkg.name}</h4>
                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${pkg.is_active ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}`}>
                    {pkg.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>
                <p className="text-sm text-slate-500 font-mono">{pkg.code}</p>
                {pkg.description && <p className="text-sm text-slate-600 mt-1">{pkg.description}</p>}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setEditingItem({ type: 'package', data: pkg })}
                  className="px-3 py-1 text-sm bg-slate-100 text-slate-700 rounded hover:bg-slate-200"
                  data-testid={`edit-package-${pkg.code}`}
                >
                  Edit
                </button>
                {pkg.is_active && (
                  <button
                    onClick={() => handleDeletePackage(pkg.code)}
                    className="px-3 py-1 text-sm bg-amber-100 text-amber-700 rounded hover:bg-amber-200"
                    data-testid={`deactivate-package-${pkg.code}`}
                  >
                    Deactivate
                  </button>
                )}
                <button
                  onClick={() => handleHardDeletePackage(pkg.code, pkg.name)}
                  className="px-3 py-1 text-sm bg-red-100 text-red-700 rounded hover:bg-red-200"
                  data-testid={`delete-package-${pkg.code}`}
                  title="Permanently delete"
                >
                  Delete
                </button>
              </div>
            </div>
            
            <div className="grid grid-cols-3 gap-4 text-sm">
              {/* Pricing Mode */}
              <div className="bg-slate-50 rounded-lg p-3">
                <div className="text-xs text-slate-500 uppercase font-medium mb-1">Pricing Mode</div>
                <div className="font-semibold text-slate-800 capitalize">{pkg.pricing_mode}</div>
                {pkg.pricing_mode === 'calculated' && pkg.calculated_config && (
                  <div className="text-xs text-slate-600 mt-1">
                    {pkg.calculated_config.labour_hours}hr @ {pkg.calculated_config.labour_rate_code}
                  </div>
                )}
                {pkg.pricing_mode === 'fixed' && pkg.fixed_config && (
                  <div className="text-xs text-slate-600 mt-1">
                    £{pkg.fixed_config.fixed_price_ex_vat.toFixed(2)} ex VAT
                  </div>
                )}
              </div>
              
              {/* Default Parts */}
              <div className="bg-slate-50 rounded-lg p-3">
                <div className="text-xs text-slate-500 uppercase font-medium mb-1">Default Parts</div>
                {pkg.default_parts?.length > 0 ? (
                  <ul className="text-xs text-slate-700 space-y-1">
                    {pkg.default_parts.map((part, i) => (
                      <li key={i}>• {part.name} (£{part.default_price_ex_vat})</li>
                    ))}
                  </ul>
                ) : (
                  <div className="text-xs text-slate-400">None</div>
                )}
              </div>
              
              {/* Part Options */}
              <div className="bg-slate-50 rounded-lg p-3">
                <div className="text-xs text-slate-500 uppercase font-medium mb-1">Part Options</div>
                {pkg.part_options?.length > 0 ? (
                  <ul className="text-xs text-slate-700 space-y-1">
                    {pkg.part_options.map((opt, i) => (
                      <li key={i}>
                        • {opt.name} (£{opt.default_price_ex_vat})
                        {opt.is_default && <span className="text-green-600 ml-1">✓ default</span>}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div className="text-xs text-slate-400">None</div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  // ============== MODALS ==============
  const CreateModal = () => {
    const [formData, setFormData] = useState(
      createType === 'labour' ? { code: '', name: '', rate_ex_vat: 60, vat_rate: 0.2, labour_category: '', sort_order: 100 } :
      createType === 'fee' ? { code: '', name: '', default_price_ex_vat: 5, vat_rate: 0.2 } :
      createType === 'package' ? { 
        code: '', name: '', description: '', pricing_mode: 'calculated',
        calculated_config: { labour_rate_code: 'LAB_MECH', labour_hours: 1, included_fee_codes: [] },
        default_parts: [], part_options: []
      } : {}
    );

    const handleSubmit = (e) => {
      e.preventDefault();
      if (createType === 'labour') handleCreateLabourRate(formData);
      else if (createType === 'fee') handleCreateFee(formData);
      else if (createType === 'package') handleCreatePackage(formData);
    };

    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowCreateModal(false)}>
        <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
          <div className="p-4 border-b border-slate-200">
            <h3 className="text-lg font-semibold text-slate-800">
              Create {createType === 'labour' ? 'Labour Rate' : createType === 'fee' ? 'Fee' : 'Package'}
            </h3>
          </div>
          
          <form onSubmit={handleSubmit} className="p-4 space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Code *</label>
              <input
                type="text"
                value={formData.code}
                onChange={e => setFormData({ ...formData, code: e.target.value })}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder={createType === 'labour' ? 'LAB_CUSTOM' : createType === 'fee' ? 'FEE_CUSTOM' : 'PKG_CUSTOM'}
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Name *</label>
              <input
                type="text"
                value={formData.name}
                onChange={e => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>
            
            {createType === 'labour' && (
              <>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Hourly Rate (ex VAT) *</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.rate_ex_vat}
                    onChange={e => setFormData({ ...formData, rate_ex_vat: parseFloat(e.target.value) })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Labour Category</label>
                  <select
                    value={formData.labour_category || ''}
                    onChange={e => setFormData({ ...formData, labour_category: e.target.value || null })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    data-testid="labour-category-select"
                  >
                    <option value="">None (not linked to a category)</option>
                    <option value="strip_renew">Strip & Renew</option>
                    <option value="service_labour">Service Labour</option>
                    <option value="diagnostics">Diagnostics</option>
                    <option value="inspection_testing">Inspection & Testing</option>
                    <option value="programming_coding">Programming & Coding</option>
                    <option value="wheel_alignment">Wheel Alignment</option>
                    <option value="aircon_work">A/C Work</option>
                    <option value="tyres_wheels">Tyres & Wheels</option>
                    <option value="body_trim">Body & Trim</option>
                    <option value="electrical">Electrical</option>
                  </select>
                  <p className="text-xs text-slate-400 mt-1">Links this rate to a labour type so pricing auto-populates correctly</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Sort Order</label>
                  <input
                    type="number"
                    value={formData.sort_order}
                    onChange={e => setFormData({ ...formData, sort_order: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </>
            )}
            
            {createType === 'fee' && (
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Price (ex VAT) *</label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.default_price_ex_vat}
                  onChange={e => setFormData({ ...formData, default_price_ex_vat: parseFloat(e.target.value) })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>
            )}
            
            {createType === 'package' && (
              <>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
                  <textarea
                    value={formData.description}
                    onChange={e => setFormData({ ...formData, description: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    rows={2}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Pricing Mode *</label>
                  <select
                    value={formData.pricing_mode}
                    onChange={e => setFormData({ ...formData, pricing_mode: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="calculated">Calculated (Labour Rate × Hours)</option>
                    <option value="fixed">Fixed Price</option>
                  </select>
                </div>
                {formData.pricing_mode === 'calculated' && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Labour Rate Code *</label>
                      <select
                        value={formData.calculated_config?.labour_rate_code || 'LAB_MECH'}
                        onChange={e => setFormData({ 
                          ...formData, 
                          calculated_config: { ...formData.calculated_config, labour_rate_code: e.target.value }
                        })}
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        {labourRates.filter(r => r.is_active).map(r => (
                          <option key={r.code} value={r.code}>{r.name} (£{r.rate_ex_vat}/hr)</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Labour Hours *</label>
                      <input
                        type="number"
                        step="0.25"
                        value={formData.calculated_config?.labour_hours || 1}
                        onChange={e => setFormData({ 
                          ...formData, 
                          calculated_config: { ...formData.calculated_config, labour_hours: parseFloat(e.target.value) }
                        })}
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        required
                      />
                    </div>
                  </>
                )}
                {formData.pricing_mode === 'fixed' && (
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Fixed Price (ex VAT) *</label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.fixed_config?.fixed_price_ex_vat || 0}
                      onChange={e => setFormData({ 
                        ...formData, 
                        fixed_config: { fixed_price_ex_vat: parseFloat(e.target.value), vat_rate: 0.2 }
                      })}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      required
                    />
                  </div>
                )}
              </>
            )}
            
            <div className="flex justify-end gap-3 pt-4 border-t border-slate-200">
              <button
                type="button"
                onClick={() => setShowCreateModal(false)}
                className="px-4 py-2 text-slate-700 hover:bg-slate-100 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                data-testid="submit-create-btn"
              >
                Create
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  const EditModal = () => {
    const [formData, setFormData] = useState(editingItem?.data || {});

    const handleSubmit = (e) => {
      e.preventDefault();
      if (editingItem.type === 'labour') {
        handleUpdateLabourRate(editingItem.data.code, {
          name: formData.name,
          rate_ex_vat: formData.rate_ex_vat,
          vat_rate: formData.vat_rate,
          labour_category: formData.labour_category || null,
          sort_order: formData.sort_order,
          is_active: formData.is_active,
        });
      } else if (editingItem.type === 'fee') {
        handleUpdateFee(editingItem.data.code, {
          name: formData.name,
          default_price_ex_vat: formData.default_price_ex_vat,
          vat_rate: formData.vat_rate,
          is_active: formData.is_active,
        });
      } else if (editingItem.type === 'package') {
        handleUpdatePackage(editingItem.data.code, formData);
      }
    };

    if (!editingItem) return null;

    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setEditingItem(null)}>
        <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
          <div className="p-4 border-b border-slate-200">
            <h3 className="text-lg font-semibold text-slate-800">
              Edit {editingItem.type === 'labour' ? 'Labour Rate' : editingItem.type === 'fee' ? 'Fee' : 'Package'}
            </h3>
          </div>
          
          <form onSubmit={handleSubmit} className="p-4 space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Code</label>
              <input
                type="text"
                value={formData.code}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg bg-slate-100 text-slate-500"
                disabled
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Name *</label>
              <input
                type="text"
                value={formData.name}
                onChange={e => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>
            
            {editingItem.type === 'labour' && (
              <>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Hourly Rate (ex VAT) *</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.rate_ex_vat}
                    onChange={e => setFormData({ ...formData, rate_ex_vat: parseFloat(e.target.value) })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">VAT Rate (%)</label>
                  <select
                    value={formData.vat_rate || 0.2}
                    onChange={e => setFormData({ ...formData, vat_rate: parseFloat(e.target.value) })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="0">0% (Zero-rated)</option>
                    <option value="0.05">5% (Reduced)</option>
                    <option value="0.2">20% (Standard)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Labour Category</label>
                  <select
                    value={formData.labour_category || ''}
                    onChange={e => setFormData({ ...formData, labour_category: e.target.value || null })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    data-testid="edit-labour-category-select"
                  >
                    <option value="">None (not linked to a category)</option>
                    <option value="strip_renew">Strip & Renew</option>
                    <option value="service_labour">Service Labour</option>
                    <option value="diagnostics">Diagnostics</option>
                    <option value="inspection_testing">Inspection & Testing</option>
                    <option value="programming_coding">Programming & Coding</option>
                    <option value="wheel_alignment">Wheel Alignment</option>
                    <option value="aircon_work">A/C Work</option>
                    <option value="tyres_wheels">Tyres & Wheels</option>
                    <option value="body_trim">Body & Trim</option>
                    <option value="electrical">Electrical</option>
                  </select>
                  <p className="text-xs text-slate-400 mt-1">Links this rate to a labour type so pricing auto-populates correctly</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Sort Order</label>
                  <input
                    type="number"
                    value={formData.sort_order}
                    onChange={e => setFormData({ ...formData, sort_order: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </>
            )}
            
            {editingItem.type === 'fee' && (
              <>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Price (ex VAT) *</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.default_price_ex_vat}
                    onChange={e => setFormData({ ...formData, default_price_ex_vat: parseFloat(e.target.value) })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">VAT Rate (%)</label>
                  <select
                    value={formData.vat_rate || 0.2}
                    onChange={e => setFormData({ ...formData, vat_rate: parseFloat(e.target.value) })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="0">0% (Zero-rated)</option>
                    <option value="0.05">5% (Reduced)</option>
                    <option value="0.2">20% (Standard)</option>
                  </select>
                </div>
              </>
            )}
            
            {editingItem.type === 'package' && (
              <>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
                  <textarea
                    value={formData.description || ''}
                    onChange={e => setFormData({ ...formData, description: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    rows={2}
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Pricing Mode</label>
                  <select
                    value={formData.pricing_mode || 'calculated'}
                    onChange={e => setFormData({ ...formData, pricing_mode: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="calculated">Calculated (Labour + Parts)</option>
                    <option value="fixed">Fixed Price</option>
                  </select>
                </div>
                
                {formData.pricing_mode === 'calculated' && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Labour Rate Code</label>
                      <select
                        value={formData.calculated_config?.labour_rate_code || 'LAB_MECH'}
                        onChange={e => setFormData({ 
                          ...formData, 
                          calculated_config: { 
                            ...formData.calculated_config, 
                            labour_rate_code: e.target.value 
                          }
                        })}
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        {labourRates.filter(r => r.is_active).map(r => (
                          <option key={r.code} value={r.code}>{r.name} (£{r.rate_ex_vat}/hr)</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Labour Hours</label>
                      <input
                        type="number"
                        step="0.1"
                        value={formData.calculated_config?.labour_hours || 1}
                        onChange={e => setFormData({ 
                          ...formData, 
                          calculated_config: { 
                            ...formData.calculated_config, 
                            labour_hours: parseFloat(e.target.value) 
                          }
                        })}
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  </>
                )}
                
                {formData.pricing_mode === 'fixed' && (
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Fixed Price (ex VAT)</label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.fixed_config?.fixed_price_ex_vat || 0}
                      onChange={e => setFormData({ 
                        ...formData, 
                        fixed_config: { fixed_price_ex_vat: parseFloat(e.target.value), vat_rate: 0.2 }
                      })}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                )}
              </>
            )}
            
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="is_active"
                checked={formData.is_active}
                onChange={e => setFormData({ ...formData, is_active: e.target.checked })}
                className="w-4 h-4 text-blue-600 border-slate-300 rounded focus:ring-blue-500"
              />
              <label htmlFor="is_active" className="text-sm font-medium text-slate-700">Active</label>
            </div>
            
            <div className="flex justify-end gap-3 pt-4 border-t border-slate-200">
              <button
                type="button"
                onClick={() => setEditingItem(null)}
                className="px-4 py-2 text-slate-700 hover:bg-slate-100 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                data-testid="submit-edit-btn"
              >
                Save Changes
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  // ============== MAIN RENDER ==============
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-slate-600">Loading Price Book...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="price-book-admin">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-slate-800">Price Book</h1>
              <p className="text-sm text-slate-500">Manage labour rates, fees, and service packages</p>
            </div>
            <a
              href="/app/foh/dashboard"
              className="px-4 py-2 text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded-lg transition-colors"
            >
              ← Back to Dashboard
            </a>
          </div>
          
          {/* Tabs */}
          <div className="flex gap-1 mt-4">
            {TABS.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  activeTab === tab.id 
                    ? 'bg-blue-600 text-white' 
                    : 'text-slate-600 hover:bg-slate-100'
                }`}
                data-testid={`tab-${tab.id}`}
              >
                <span className="mr-2">{tab.icon}</span>
                {tab.label}
                <span className="ml-2 px-2 py-0.5 rounded-full text-xs bg-white/20">
                  {tab.id === 'labour' ? labourRates.length : tab.id === 'fees' ? fees.length : packages.length}
                </span>
              </button>
            ))}
          </div>
        </div>
      </div>
      
      {/* Content */}
      <div className="max-w-6xl mx-auto px-4 py-6">
        {activeTab === 'labour' && renderLabourRatesTab()}
        {activeTab === 'fees' && renderFeesTab()}
        {activeTab === 'packages' && renderPackagesTab()}
      </div>
      
      {/* Modals */}
      {showCreateModal && <CreateModal />}
      {editingItem && <EditModal />}
    </div>
  );
}
